<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header variant="cross" :saving="loading" @cancel="closeModal()">
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-8">
        <div class="flex flex-col w-full">
          <div class="flex flex-col gap-y-5">
            <FormInput
              v-model="rawStatisticPoint.name"
              :error="errors?.name"
              class="col-start-1 col-end-2"
              type="text"
              :label="$t('statisticPoint.name')"
              :placeholder="$t('statisticPoint.name')"
            />
            <Multiselect
              v-model="rawStatisticPoint.campaign"
              :error="errors?.campaign"
              class="col-start-1 col-end-2"
              :options="campaigns"
              :label="$t('statisticPoint.campaign')"
              :can-deselect="false"
              :can-clear="false"
              :searchable="true"
              @select="selectCampaign()"
            />
            <Multiselect
              v-if="isNew"
              v-model="rawStatisticPoint.template"
              :error="errors?.template"
              class="col-start-1 col-end-2"
              :options="statisticPointTemplates"
              :label="$t('statisticPoint.template')"
              :can-deselect="true"
              :can-clear="true"
              :searchable="true"
              :object="true"
              :loading="loadingStatisticPointTemplates"
              :disabled="!rawStatisticPoint.campaign"
              @select="selectTemplate"
            />
            <LocationMap
              ref="map"
              v-model="rawStatisticPoint.gisLocation"
              v-model:radius="rawStatisticPoint.radius"
            />
            <div class="flex gap-x-5">
              <FormInput
                :model-value="rawStatisticPoint.gisLocation?.lat"
                :error="errors?.gisLocation?.lat"
                class="w-1/2"
                type="text"
                :label="$t('lat')"
                pattern="^\d*\.?\d*$"
                @update:model-value="setCoords(parseFloat($event), rawStatisticPoint.gisLocation?.long)"
              />
              <FormInput
                :model-value="rawStatisticPoint.gisLocation?.long"
                :error="errors?.gisLocation?.long"
                class="w-1/2"
                type="text"
                :label="$t('long')"
                pattern="^\d*\.?\d*$"
                @update:model-value="setCoords(rawStatisticPoint.gisLocation?.lat, parseFloat($event))"
              />
            </div>
            <FormInput
              v-model="rawStatisticPoint.radius"
              :error="errors?.radius"
              class="col-start-1 col-end-2"
              type="number"
              :label="$t('statisticPoint.radius')"
              :placeholder="$t('statisticPoint.radius')"
            />
            <div class="grid grid-cols-2 gap-x-5">
              <DatePicker
                v-model="rawStatisticPoint.validityStart"
                :error="errors?.validityStart"
                mode="datetime"
                :label="$t('statisticPoint.validityStart')"
              />
              <DatePicker
                v-model="rawStatisticPoint.validityEnd"
                :error="errors?.validityEnd"
                mode="datetime"
                :label="$t('statisticPoint.validityEnd')"
              />
            </div>
          </div>
        </div>
        <div class="w-full text-center">
          <custom-button
            variant="primary"
            class="mt-15"
            :busy="loading"
            @click="save"
          >
            {{ $t('save') }}
          </custom-button>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import {
  computed, reactive, ref, watch,
} from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import {
  date, number, object, string,
} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import FormInput from '@utils/inputs/Input.vue';
import Multiselect from '@utils/Multiselect.vue';
import DatePicker from '@components/utils/inputs/DatePicker.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import { syncRef } from '@hooks/state';
import CustomButton from '@utils/CustomButton.vue';
import { collOptionsProvider } from '@utils/table/hooks/provider';
import LocationMap from '@utils/maps/PickLocationRadiusMap.vue';
import type {
  Campaign,
  CampaignGroup,
  StatisticPoint,
  StatisticPointTemplate,
} from '@/entities/Entities';

interface Props {
  open: boolean;
  campaignGroup?: CampaignGroup;
  statisticPoint?: StatisticPoint;
}

const emit = defineEmits(['update:open', 'update:statisticPoint', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalStatisticPoint = useVModel(props, 'statisticPoint', emit);

const isNew = computed(() => !props.statisticPoint);
const tKey = computed(() =>
  (isNew.value
    ? 'pages.campaigns.statisticPointsList.modal.new'
    : 'pages.campaigns.statisticPointsList.modal.edit'));

const { t } = useI18n();

const notify = useNotify();

const groupId = computed<{ group?: string }>(() =>
  (props.campaignGroup?.id ? { group: props.campaignGroup.id } : {}));

const params = reactive({
  filter: groupId,
});

const fetchCampaigns = collOptionsProvider('/campaigns', params, (value: Campaign) => ({
  label: value.name,
  value: value['@id'],
  gisLocation: value.region?.gisLocation,
  region: value.region,
}));

const campaigns = ref<{
  label: string,
  value: string | null,
  gisLocation?: { lat: number, long: number }
}[]>([]);
const loadCampaigns = async () => {
  campaigns.value = await fetchCampaigns();
};

if (props.campaignGroup?.id) {
  loadCampaigns();
}
watch(groupId, loadCampaigns, { immediate: false });

const initialData = (statisticPoint?: StatisticPoint) =>
  (statisticPoint
    ? {
      ...statisticPoint,
      template: {
        label: statisticPoint.template?.name,
        value: statisticPoint.template?.['@id'],
        item: statisticPoint.template,
      },
    }
    : {
      campaign: undefined,
      name: undefined,
      radius: undefined,
      gisLocation: undefined,
      validityStart: undefined,
      validityEnd: undefined,
      template: undefined,
    });

const rawStatisticPoint = syncRef(internalStatisticPoint, (statisticPoint) => ({
  ...initialData(statisticPoint),
}));

const {
  data: statisticPoint,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    campaign: string()
      .nullable()
      .required(t('validation.notEmpty')),
    name: string()
      .nullable()
      .required(t('validation.notEmpty')),
    radius: number()
      .typeError(t('validation.number'))
      .positive(t('validation.positive'))
      .integer(t('validation.integer'))
      .required(t('validation.notEmpty')),
    gisLocation: object({
      lat: number()
        .typeError(t('validation.number'))
        .nullable()
        .required(t('validation.notEmpty')),
      long: number()
        .typeError(t('validation.number'))
        .nullable()
        .required(t('validation.notEmpty')),
    }),
    validityStart: date()
      .nullable(),
    validityEnd: date()
      .nullable(),
  }),
  rawStatisticPoint,
);

const statisticPointTemplates = ref<{
  label: string,
  value: string,
  item: StatisticPointTemplate
}>([]);
const loadingStatisticPointTemplates = ref(false);
const loadStatisticPointTemplates = async (regionId: string) => {
  loadingStatisticPointTemplates.value = true;
  try {
    statisticPointTemplates.value = await collOptionsProvider('/statistic_point_templates', { filter: { region: regionId } }, (value: StatisticPointTemplate) => ({
      label: value.name,
      value: value['@id'],
      item: value,
    }))();
  } finally {
    loadingStatisticPointTemplates.value = false;
  }
};

function resetData() {
  rawStatisticPoint.value = { ...initialData() } as unknown as typeof statisticPoint['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalStatisticPoint.value = undefined;
}

const map = ref();

const setMapCenter = () => {
  const gisLocation = campaigns.value.find((campaign) => campaign.value === rawStatisticPoint.value?.campaign)?.gisLocation;
  map.value?.moveToCenter(gisLocation?.lat, gisLocation?.long);
};

const selectCampaign = () => {
  setMapCenter();
  loadStatisticPointTemplates(campaigns.value.find((campaign) => campaign.value === rawStatisticPoint.value?.campaign)?.region?.id);
};

const selectTemplate = () => {
  rawStatisticPoint.value.name = rawStatisticPoint.value.template?.item?.name ?? undefined;
  rawStatisticPoint.value.radius = rawStatisticPoint.value.template?.item?.radius ?? undefined;
  rawStatisticPoint.value.validityStart = rawStatisticPoint.value.template?.item?.validityStart ?? undefined;
  rawStatisticPoint.value.validityEnd = rawStatisticPoint.value.template?.item?.validityEnd ?? undefined;
  rawStatisticPoint.value.gisLocation = rawStatisticPoint.value.template?.item?.gisLocation ?? undefined;
};

const setCoords = (lat: number | undefined, long: number | undefined) => {
  rawStatisticPoint.value.gisLocation = {
    lat: lat || 0,
    long: long || 0,
  };
};

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    const edit = !!props.statisticPoint;
    const url = edit ? props.statisticPoint['@id'] : '/statistic_points';

    await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        campaign: statisticPoint.value.campaign,
        name: statisticPoint.value.name,
        radius: statisticPoint.value.radius,
        gisLocation: statisticPoint.value.gisLocation,
        validityStart: statisticPoint.value.validityStart,
        validityEnd: statisticPoint.value.validityEnd,
        template: statisticPoint.value.template?.value,
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t(`${tKey.value}.saveError`),
    });
  }
});
</script>

<style lang="scss" scoped></style>
